import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { generatePath, useNavigate } from 'react-router';
import styled from 'styled-components';
import { saveAs } from 'file-saver';
import axios, { AxiosError, AxiosResponse } from 'axios';
import { useApi } from '@sdir/httpclient/lib/hooks/useApi';
import {
  AlertBox,
  DataTable,
  Loader,
  SdiDownloadFile,
  SdiLicense,
  SdirButton,
  SdirDatePicker,
  SdiReport,
  SdirModal,
  Select,
  SelectOption,
  styles,
  TextLink,
} from '@sdir/sds';
import {
  formatDateString,
  formatDateTimeString,
  sortListByAttribute,
} from '@sdir/blueprint.aps/lib/helpers/formattingHelpers';
import {
  CRA,
  CustomStatusLabel,
  Dispensation,
  Endorsement,
} from '@sdir/blueprint.aps/lib/components';
import { useCurrentUser } from '@sdir/auth/lib/hooks';
import { ErrorMessage } from '@sdir/utilities/lib/components';
// eslint-disable-next-line max-len
import {
  EndorsementServiceFeaturesGetOwnApplicationsV2ApplicationResponse as ApplicationDto,
  EndorsementServiceFeaturesGetOwnApplicationsV2GetOwnApplicationResponse as ApplicationGroupDto,
} from '@sdir/httpclient/lib/clients/aps/endorsement';
import {
  ApplicationChildTableHeader,
  ApplicationTableHeader,
  ExternalApplicationTableHeader,
  MobileApplicationTableHeader,
} from '../../helpers/TableHeaders';
import ApplicationTable, {
  DEFAULT_DESC,
  DEFAULT_ORDERBY,
  DEFAULT_PAGESIZE,
} from '../Molecules/ApplicationTable';
import { routes } from '../Routes';
import { endorsementApi } from '../../httpclient';
import { ApplicationStatus, FilterApplicationStatus } from '../../constants/enums';
import { useReportees } from '../../services/hooks/useReportees';
import { getStatusLabelText, getStatusLabelVariant } from '../../helpers/applicationHelpers';
import ClarifyModal, { ClarifyInput } from '../Organisms/ClarifyModal';
import { useNationalities } from '../../services/hooks/useNationalities';
import { formatDateStringWithoutTimezone } from '../../helpers/formattingHelpers';
import { useScreenUtils } from '../../services/hooks/useScreenUtils';
import localization from '../../localization/localization';
import { checkIsOrganization, ExternalUserRole, PersonRole } from '../../constants/permissions';
import { getReceiptLanguage } from '../../helpers/localizationHelpers';

type PayloadType = {
  skip?: number;
  take?: number;
  type?: string;
  status?: string;
  seafareremail?: string;
  reporteeId?: string;
  orderby?: string;
  descending?: boolean;
  paidAt?: string;
  paidAtTo?: string;
  nationality?: string;
};

const HomePage: React.FC = () => {
  const intl = useIntl();
  const navigate = useNavigate();
  const {
    data: { result: reportees, loading: reporteesLoading },
  } = useReportees();
  const currentUser = useCurrentUser();
  const { result: nationalities, loading: nationalitiesLoading } = useNationalities();
  const { isMobile } = useScreenUtils();
  const [currentFilter, setCurrentFilter] = useState<any>({});
  const [showCra, setShowCra] = useState<boolean>(false);
  const [showEndorsement, setShowEndorsement] = useState<boolean>(false);
  const [showDispensation, setShowDispensation] = useState<boolean>(false);
  const [showRejectedReason, setShowRejectedReason] = useState<boolean>(false);
  const [showClarifyModal, setShowClarifyModal] = useState<boolean>(false);
  const [showConfirmDelete, setShowConfirmDelete] = useState<boolean>(false);
  const [receiptError, setReceiptError] = useState<any>();
  const [selectedGroup, setSelectedGroup] = useState<ApplicationGroupDto | null>();
  const [selectedApplication, setSelectedApplication] = useState<ApplicationDto | null>();
  const [currentPage, setCurrentPage] = useState<number>(0);

  const isExternalUser = useMemo(() => {
    const role = currentUser?.profile?.role;
    const hasExternalRole = role?.includes(ExternalUserRole);
    const hasPersonRole = role?.includes(PersonRole);
    const hasOrgRole = checkIsOrganization(currentUser);
    return hasExternalRole && !hasPersonRole && !hasOrgRole;
  }, [currentUser?.profile?.role]);

  const tableHeaders = useMemo(() => {
    if (isMobile) return MobileApplicationTableHeader;
    if (isExternalUser) return ExternalApplicationTableHeader;
    return ApplicationTableHeader;
  }, [isMobile, isExternalUser]);

  useEffect(() => {
    const { search } = window.location;
    if (search.indexOf('transactionId') !== -1 || search.indexOf('responseCode') !== -1) {
      window.history.replaceState({}, document.title, `${window.location.pathname}#/`);
      window.sessionStorage.setItem('payment_window_step', '2');
      navigate({
        pathname: window.sessionStorage.getItem('application_payment_step_url') as string,
        search,
      });
    }
  }, [navigate]);

  const {
    result: applications,
    errorObject: applicationsError,
    loading: applicationsLoading,
    callApi: getApplications,
  } = useApi({
    apiFunction: (payload?: PayloadType) =>
      endorsementApi.getOwnApplicationsV2(
        payload?.orderby || DEFAULT_ORDERBY,
        payload?.status,
        payload?.descending,
        payload?.seafareremail,
        payload?.nationality,
        payload?.paidAt,
        payload?.paidAtTo,
        payload?.skip || 0,
        payload?.take || DEFAULT_PAGESIZE
      ),
    callApiOnInit: false,
  });

  const {
    result: clarifyApplication,
    errorObject: applicationError,
    loading: applicationLoading,
    callApi: getApplication,
  } = useApi({
    apiFunction: (uid) => endorsementApi.getApplicationByIdV2(uid),
    callApiOnInit: false,
    successCallback: () => {
      setShowClarifyModal(true);
    },
  });

  const {
    errorObject: clarifySubmitError,
    loading: clarifySubmitLoading,
    callApi: submitClarification,
  } = useApi({
    apiFunction: (payload) =>
      endorsementApi.uploadAdditionalInformation(
        payload.uid,
        payload.nisCourse,
        payload.nationalCertificate,
        payload.comment,
        payload.vesselGrossTonnageLessThan250,
        payload.dispensation,
        payload.declarationOfCompetence
      ),
    callApiOnInit: false,
    successCallback: () => {
      onTableChange({});
      setShowClarifyModal(false);
    },
  });

  const {
    result: seafarers,
    errorObject: seafarersError,
    loading: seafarersLoading,
  } = useApi({
    apiFunction: () => endorsementApi.getOwnSeafarers(),
    callApiOnInit: true,
  });

  const {
    errorObject: deleteError,
    loading: deleteLoading,
    callApi: deleteApplicationGroup,
  } = useApi({
    apiFunction: (uid) => endorsementApi.removeApplicationGroup(uid),
    callApiOnInit: false,
    successCallback: () => {
      onTableChange({});
      onCloseConfirmDelete();
    },
  });

  const { loading: loadingCraPdf, callApi: generateCraPdf } = useApi({
    apiFunction: (uid) =>
      endorsementApi.downloadIssuedDocuments(uid, 'cra', {
        responseType: 'blob',
      }),
    callApiOnInit: false,
    successCallback: (response: any) => {
      saveAs(response, `CRA.pdf`);
    },
  });

  const { loading: loadingDispensationPdf, callApi: generateDispensation } = useApi({
    apiFunction: (uid) =>
      endorsementApi.downloadIssuedDocuments(uid, 'dispensation', {
        responseType: 'blob',
      }),
    callApiOnInit: false,
    successCallback: (response: any) => {
      saveAs(response, `Dispensation.pdf`);
    },
  });

  const { loading: loadingEndorsementPdf, callApi: generateEndorsementPdf } = useApi({
    apiFunction: (uid) =>
      endorsementApi.downloadIssuedDocuments(uid, 'issuedendorsement', {
        responseType: 'blob',
      }),
    callApiOnInit: false,
    successCallback: (response: any) => {
      saveAs(response, `IssuedEndorsement.pdf`);
    },
  });

  const downloadPaymentReceipt = useCallback(
    (paidAt: string, paymentId: string) => {
      const token = currentUser?.access_token || '';
      const url = `${process.env.REACT_APP_API_URL_PAYMENT_WINDOW}payment/receiptDocument`;
      setReceiptError(undefined);
      axios
        .post(
          url,
          {
            id: paymentId,
            language: { language: getReceiptLanguage(intl?.locale || '') },
          },
          {
            responseType: 'blob',
            headers: {
              'Accept-Language': intl?.locale || 'no',
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
          }
        )
        .then((response: AxiosResponse) => {
          if (response.status >= 400 && response.status < 600) {
            const err = new Error(response.statusText);
            setReceiptError(err);
            throw err;
          }
          const bloburl = window.URL.createObjectURL(
            new Blob([response.data], { type: 'application/octet-stream' })
          );
          return bloburl;
        })
        .then((blobUrl: string) => {
          const link = document.createElement('a');
          link.href = blobUrl;
          const paymentDate = paidAt?.substring(0, 10);
          const filename = `Payment_Receipt_${paymentDate}.pdf`;
          link.setAttribute('download', filename);

          const clickHandler = () => {
            setTimeout(() => {
              URL.revokeObjectURL(blobUrl);
              link.removeEventListener('click', clickHandler);
              document.body.removeChild(link);
            }, 150);
          };

          document.body.appendChild(link);
          link.addEventListener('click', clickHandler, false);
          link.click();
        })
        .catch((e: AxiosError) => {
          setReceiptError(e);
        });
    },
    [currentUser, intl?.locale]
  );

  const hasNoAccess = applicationsError?.response?.data?.status === 403;

  const onNewApplication = () => {
    const path = generatePath(routes.applicationNew);
    navigate(path);
  };

  const onEnterCode = () => {
    const path = generatePath(routes.code);
    navigate(path);
  };

  const onEdit = (uid?: string) => {
    if (!uid) return;
    const hasReportees = !!reportees?.length;
    const route = hasReportees ? routes.applicant : routes.sailor;
    const path = generatePath(route, { applicationUid: uid });
    navigate(path);
  };

  const onCloseConfirmDelete = () => {
    setShowConfirmDelete(false);
    setSelectedGroup(undefined);
  };

  const onDelete = (g?: any) => {
    if (!g?.id) return;
    setSelectedGroup(g);
    setShowConfirmDelete(true);
  };

  const onConfirmDelete = () => {
    const uid = selectedGroup?.id;
    if (!uid) return;
    deleteApplicationGroup(uid);
  };

  const onClarify = (uid?: string) => {
    if (!uid) return;
    getApplication(uid);
  };

  const onPayment = (uid?: string) => {
    if (!uid) return;
    const path = generatePath(routes.payment, { applicationUid: uid });
    navigate(path);
  };

  const onSummary = (uid?: string) => {
    if (!uid) return;
    const path = generatePath(routes.summary, { applicationUid: uid });
    navigate(path);
  };

  const onOpenRejectedReason = (a: any, g: any) => {
    setSelectedApplication(a);
    setSelectedGroup(g);
    setShowRejectedReason(true);
  };

  const onOpenCra = (a: any, g: any) => {
    setSelectedApplication(a);
    setSelectedGroup(g);
    setShowCra(true);
  };

  const onOpenDispensation = (a: any, g: any) => {
    setSelectedApplication(a);
    setSelectedGroup(g);
    setShowDispensation(true);
  };

  const onOpenEndorsement = (a: any, g: any) => {
    setSelectedApplication(a);
    setSelectedGroup(g);
    setShowEndorsement(true);
  };

  const onCloseRejectedReason = () => {
    setSelectedApplication(undefined);
    setSelectedGroup(undefined);
    setShowRejectedReason(false);
  };

  const onCloseCra = () => {
    setSelectedApplication(undefined);
    setSelectedGroup(undefined);
    setShowCra(false);
  };

  const onCloseDispensation = () => {
    setSelectedApplication(undefined);
    setSelectedGroup(undefined);
    setShowDispensation(false);
  };

  const onCloseEndorsement = () => {
    setSelectedApplication(undefined);
    setSelectedGroup(undefined);
    setShowEndorsement(false);
  };

  const downloadCra = () => {
    if (selectedApplication?.id) {
      generateCraPdf(selectedApplication.id);
    }
  };

  const downloadDispensation = () => {
    if (selectedApplication?.id) {
      generateDispensation(selectedApplication.id);
    }
  };

  const downloadEndorsement = () => {
    if (selectedApplication?.id) {
      generateEndorsementPdf(selectedApplication.id);
    }
  };

  const onSubmitClarify = (input: ClarifyInput) => {
    submitClarification(input);
  };

  const onTableChange = (settings) => {
    const {
      page,
      orderBy,
      direction,
      nationality,
      skip,
      take,
      paidAt,
      paidAtTo,
      seafareremail,
      status,
    } = settings;
    const newPage = page !== undefined ? page : 0;
    const newSkip = skip || newPage * DEFAULT_PAGESIZE;
    const newTake = take || DEFAULT_PAGESIZE;
    const newDescending = direction
      ? direction === 1
      : 'descending' in currentFilter
      ? !!currentFilter.descending
      : DEFAULT_DESC;
    let newSeafarer = seafareremail || currentFilter?.seafareremail || undefined;
    newSeafarer = newSeafarer === 'none' ? null : newSeafarer;
    let newStatus = status || currentFilter?.status || undefined;
    newStatus = newStatus === 'none' ? null : newStatus;
    let newNationality = nationality || currentFilter?.nationality || undefined;
    newNationality = newNationality === 'none' ? null : newNationality;
    const newOrderBy = orderBy || currentFilter?.orderBy || DEFAULT_ORDERBY;

    const paidAtDate = paidAt as Date;
    const payloadPaidAt = paidAt
      ? formatDateStringWithoutTimezone(paidAtDate.toString())
      : currentFilter?.paidAt
      ? formatDateStringWithoutTimezone(currentFilter.paidAt)
      : undefined;
    const newPaidAt = paidAt || currentFilter?.paidAt;

    const paidAtToDate = paidAtTo as Date;
    const payloadPaidAtTo = paidAtTo
      ? formatDateStringWithoutTimezone(paidAtToDate.toString())
      : currentFilter?.paidAtTo
      ? formatDateStringWithoutTimezone(currentFilter.paidAtTo)
      : undefined;
    const newPaidAtTo = paidAtTo || currentFilter?.paidAtTo;

    const payload: PayloadType = {
      skip: newSkip,
      take: newTake,
      type: undefined,
      status: newStatus,
      seafareremail: newSeafarer,
      reporteeId: undefined,
      orderby: newOrderBy,
      descending: newDescending,
      nationality: newNationality,
      paidAt: payloadPaidAt,
      paidAtTo: payloadPaidAtTo,
    };
    const newfilter = {
      ...currentFilter,
      orderBy: newOrderBy,
      descending: newDescending,
      seafareremail: newSeafarer,
      status: newStatus,
      paidAt: newPaidAt,
      paidAtTo: newPaidAtTo,
      nationality: newNationality,
    };
    setCurrentFilter(newfilter);
    getApplications(payload);
    setCurrentPage(newPage);
  };

  const onClearFilter = () => {
    setCurrentFilter({});
    getApplications({});
  };

  const statusOptions: SelectOption[] = useMemo(() => {
    const noSelection: SelectOption[] = [{ name: 'No selection', value: 'none' }];
    let statuses: SelectOption[] = Object.entries(FilterApplicationStatus).map((s) => ({
      name: intl.formatMessage({ id: `application.filter.status.${s[0]}` }),
      value: s[1],
    }));
    statuses = sortListByAttribute(statuses, 'name');
    return noSelection.concat(statuses);
  }, [intl]);

  const seafarersOptions: SelectOption[] = useMemo(() => {
    if (!seafarers) return [];
    const noSelection: SelectOption[] = [{ name: 'No selection', value: 'none' }];
    let options: SelectOption[] = seafarers?.map((s) => ({
      name: s.name || '',
      value: s.email || '',
      secondValue: formatDateString(s.dateOfBirth || ''),
    }));
    options = sortListByAttribute(options, 'name');
    return noSelection.concat(options);
  }, [seafarers]);

  const nationalityOptions = useMemo(() => {
    if (!nationalities) return [];
    const noSelection: SelectOption[] = [{ name: 'No selection', value: 'none' }];
    let options: SelectOption[] = nationalities.map<SelectOption>((v) => ({
      name: v?.[intl?.locale] || '',
      value: v.idIso2 || '',
    }));
    options = sortListByAttribute(options, 'name');
    return noSelection.concat(options);
  }, [nationalities, intl?.locale]);

  const formattedData = useMemo(() => {
    const items = applications?.result;
    if (!items || !items?.map) return [];
    const filterRow = [
      {
        status: (
          <Select
            defaultVal={intl.formatMessage({ id: 'application.table.header.status' })}
            disabled={applicationsLoading}
            id="select-status"
            handleChange={(v) => onTableChange({ status: v })}
            options={statusOptions}
            value={currentFilter?.status}
          />
        ),
        name: (
          <Select
            defaultVal={intl.formatMessage({ id: 'application.table.header.name' })}
            disabled={applicationsLoading || seafarersLoading}
            id="select-seafarer"
            handleChange={(v) => onTableChange({ seafareremail: v })}
            options={seafarersOptions}
            value={currentFilter?.seafareremail}
            searchable
          />
        ),
        paidAt: (
          <SdirDatePicker
            startPlaceholder="Start"
            endPlaceholder="End"
            label="Payment"
            id="select-payment-range"
            startValue={currentFilter?.paidAt}
            endValue={currentFilter?.paidAtTo}
            selectRange
            onStartChange={(v) => onTableChange({ paidAt: v })}
            onEndChange={(v) => onTableChange({ paidAtTo: v })}
          />
        ),
        nationality: (
          <Select
            defaultVal={intl.formatMessage({ id: 'application.table.header.nationality' })}
            disabled={applicationsLoading || nationalitiesLoading}
            id="select-nationality"
            handleChange={(v) => onTableChange({ nationality: v })}
            options={nationalityOptions}
            value={currentFilter?.nationality}
            searchable
          />
        ),
      },
    ];
    const dataItems = items?.map((group) => {
      const childData = (group?.applications || [])?.map((a) => ({
        endorsement: a?.title || '',
        status: (
          <CustomStatusLabel
            status={getStatusLabelVariant(a?.status || '')}
            text={getStatusLabelText(a?.status || '')}
          />
        ),
        actions: (
          <ChildActions>
            {a?.rejectedReason && (
              <TextLink
                text={intl.formatMessage({ id: 'application.rejection.link' })}
                icon={<SdiReport color={styles.colors.primary} size="s" />}
                onClick={() => onOpenRejectedReason(a, group)}
              />
            )}
            {a?.paidAt && a?.paymentId && (
              <TextLink
                text={intl.formatMessage({ id: 'application.receipt.link' })}
                icon={<SdiDownloadFile color={styles.colors.primary} size="s" />}
                onClick={() => downloadPaymentReceipt(a.paidAt || '', a.paymentId || '')}
              />
            )}
            {a?.cra && (
              <TextLink
                text="CRA"
                icon={<SdiLicense color={styles.colors.primary} size="s" />}
                onClick={() => onOpenCra(a, group)}
              />
            )}
            {a?.dispensation && (
              <TextLink
                text="Dispensation"
                icon={<SdiReport color={styles.colors.primary} size="s" />}
                onClick={() => onOpenDispensation(a, group)}
              />
            )}
            {a?.endorsement && (
              <TextLink
                text="Endorsement"
                icon={<SdiLicense color={styles.colors.primary} size="s" />}
                onClick={() => onOpenEndorsement(a, group)}
              />
            )}
          </ChildActions>
        ),
      }));
      const inDraft = group?.applications?.some((i) => i.status === ApplicationStatus.Draft);
      const inProcessing = group?.applications?.some(
        (i) => i.status === ApplicationStatus.ManualProcessing
      );
      const inAwaiting = group?.applications?.some(
        (i) => i.status === ApplicationStatus.RequestMoreInformation
      );
      const clarifyUid = group?.applications?.find(
        (i) => i.status === ApplicationStatus.RequestMoreInformation
      )?.id;
      const status = inProcessing
        ? ApplicationStatus.ManualProcessing
        : inAwaiting
        ? ApplicationStatus.RequestMoreInformation
        : group?.applications?.[0]?.status;
      return {
        createdAt: formatDateString(group?.applications?.[0]?.createdAt || ''),
        name: <>{group?.seafarerName || ''}</>,
        status: (
          <CustomStatusLabel
            status={getStatusLabelVariant(status || '')}
            text={getStatusLabelText(status || '')}
          />
        ),
        paidAt: <>{formatDateTimeString(group?.applications?.[0]?.paidAt || '')}</>,
        nationality: <>{group?.seafarerNationality || ''}</>,
        contactEmail: <Applicant>{group?.contactEmail || ''}</Applicant>,
        onBehalfOf: <>{group?.reporteeName || ''}</>,
        actions: (
          <ActionsWrapper>
            {inDraft && (
              <>
                <SdirButton
                  text="Edit"
                  onClick={() => onEdit(group?.id || '')}
                  size="sm"
                  disabled={reporteesLoading || deleteLoading}
                />
                <SdirButton
                  text={intl.formatMessage({ id: 'general.delete' })}
                  className="delete-btn"
                  onClick={() => onDelete(group)}
                  size="sm"
                  disabled={reporteesLoading || deleteLoading}
                />
              </>
            )}
            {inAwaiting && (
              <SdirButton
                text="Clarify"
                onClick={() => onClarify(clarifyUid || '')}
                size="sm"
                disabled={reporteesLoading || deleteLoading}
              />
            )}
            {status === ApplicationStatus.DraftConfirmed && (
              <SdirButton text="Summary" onClick={() => onSummary(group?.id || '')} size="sm" />
            )}
            {status === ApplicationStatus.Payment && (
              <SdirButton text="Payment" onClick={() => onPayment(group?.id || '')} size="sm" />
            )}
          </ActionsWrapper>
        ),
        childContent: (
          <DataTable
            className="application-child-table"
            columns={ApplicationChildTableHeader}
            data={childData}
            sortableKeys={['endorsement']}
          />
        ),
      };
    });
    return filterRow.concat(dataItems);
  }, [applications, reporteesLoading, nationalitiesLoading, deleteLoading]);

  useEffect(() => {
    const payload: PayloadType = {
      skip: currentPage * DEFAULT_PAGESIZE,
      take: DEFAULT_PAGESIZE,
      type: undefined,
      status: currentFilter?.status || undefined,
      seafareremail: currentFilter?.seafareremail || undefined,
      reporteeId: undefined,
      orderby: currentFilter?.orderBy || DEFAULT_ORDERBY,
      descending: 'descending' in currentFilter ? !!currentFilter?.descending : DEFAULT_DESC,
    };
    getApplications(payload);
  }, []);

  const isStcwF = selectedApplication?.regulationReference === 'STCW_F';
  const hasFiltered = !!(Object.keys(currentFilter || {}).length > 0);
  const showTable = !!(
    (applications && applications.total && applications.total > 0) ||
    hasFiltered
  );
  const showEmpty = !!(
    !applicationsLoading &&
    !applicationsError &&
    applications &&
    applications.total === 0 &&
    !hasFiltered
  );

  return (
    <>
      <H1>{intl.formatMessage({ id: 'dashboard.title' })}</H1>
      <p>{intl.formatMessage({ id: 'dashboard.ingress' })}</p>
      <ActionBar>
        {!showEmpty && !hasNoAccess && (
          <SdirButton
            text={intl.formatMessage({ id: 'application.new.button' })}
            type="primary"
            disabled={applicationsLoading || reporteesLoading}
            onClick={onNewApplication}
          />
        )}
        {showTable && (
          <ClearFilter>
            <SdirButton
              type="default"
              onClick={onClearFilter}
              text={intl.formatMessage({ id: 'general.clearfilter' })}
            />
          </ClearFilter>
        )}
      </ActionBar>
      <MobileFilter>
        <Select
          defaultVal={intl.formatMessage({ id: 'application.table.header.status' })}
          disabled={applicationsLoading}
          id="select-status"
          handleChange={(v) => onTableChange({ status: v })}
          options={statusOptions}
          value={currentFilter?.status}
        />
        <Select
          defaultVal={intl.formatMessage({ id: 'application.table.header.name' })}
          disabled={applicationsLoading || seafarersLoading}
          id="select-seafarer"
          handleChange={(v) => onTableChange({ seafareremail: v })}
          options={seafarersOptions}
          value={currentFilter?.seafareremail}
          searchable
        />
        <SdirDatePicker
          startPlaceholder="Start"
          endPlaceholder="End"
          label="Payment"
          id="select-payment-range"
          startValue={currentFilter?.paidAt}
          endValue={currentFilter?.paidAtTo}
          selectRange
          onStartChange={(v) => onTableChange({ paidAt: v })}
          onEndChange={(v) => onTableChange({ paidAtTo: v })}
        />
        <Select
          defaultVal={intl.formatMessage({ id: 'application.table.header.nationality' })}
          disabled={applicationsLoading || nationalitiesLoading}
          id="select-nationality"
          handleChange={(v) => onTableChange({ nationality: v })}
          options={nationalityOptions}
          value={currentFilter?.nationality}
          searchable
        />
      </MobileFilter>
      <Applications>
        {applicationsError && !hasNoAccess && <ErrorMessage error={applicationsError} />}
        {receiptError && !hasNoAccess && <ErrorMessage error={receiptError} />}
        {showTable && (
          <ApplicationTable
            data={formattedData}
            columns={tableHeaders}
            totalCount={applications?.total || 0}
            refreshData={onTableChange}
            loading={applicationsLoading}
            currentPage={currentPage}
            sortableKeys={['createdAt', 'name', 'paidAt', 'nationality']}
          />
        )}
        {showEmpty && (
          <AlertBox
            type="discinfo"
            title={intl.formatMessage({ id: 'application.empty.title' })}
            text={intl.formatMessage({ id: 'application.empty.description' })}
            hideClose
            extraContent={
              <EmptyAction>
                <SdirButton
                  type="primary"
                  text={intl.formatMessage({ id: 'application.new.button' })}
                  onClick={onNewApplication}
                  disabled={applicationsLoading || reporteesLoading}
                />
              </EmptyAction>
            }
          />
        )}
        {hasNoAccess && (
          <AlertBox
            type="discinfo"
            title={intl.formatMessage({ id: 'application.noaccess.title' })}
            text={intl.formatMessage({ id: 'application.noaccess.description' })}
            hideClose
            extraContent={
              <EmptyAction>
                <SdirButton
                  type="primary"
                  text={intl.formatMessage({ id: 'application.code.button' })}
                  onClick={onEnterCode}
                  disabled={applicationsLoading || reporteesLoading}
                />
              </EmptyAction>
            }
          />
        )}
      </Applications>
      <StyledModal visible={showRejectedReason} onCancel={onCloseRejectedReason} width="400px">
        <div>
          <h2>{intl.formatMessage({ id: 'application.rejection.title' })}</h2>
          <Reason>{selectedApplication?.rejectedReason || ''}</Reason>
        </div>
      </StyledModal>
      <StyledModal
        visible={showConfirmDelete}
        onCancel={onCloseConfirmDelete}
        onOK={onConfirmDelete}
        okText={intl.formatMessage({ id: 'general.yes' })}
        cancelText={intl.formatMessage({ id: 'general.cancel' })}
        width="400px"
      >
        <div>
          <h2>{intl.formatMessage({ id: 'application.confirmdelete.title' })}</h2>
          <p>{intl.formatMessage({ id: 'application.confirmdelete.description' })}</p>
          {deleteError && <ErrorMessage error={deleteError} />}
        </div>
      </StyledModal>
      <StyledModal visible={showCra} onCancel={onCloseCra}>
        <div>
          <Download>
            <TextLink
              onClick={downloadCra}
              text={intl.formatMessage({ id: 'general.downloadpdf' })}
            />
            {loadingCraPdf && <Loader size="24px" />}
          </Download>
          <div translate="no">
            <CRA
              authority={selectedApplication?.cra?.authority || ''}
              nationalCertificate={selectedApplication?.cra?.nationalCapacity || ''}
              requestedCertificate={selectedApplication?.cra?.title || ''}
              certificateNr={selectedApplication?.cra?.certificateNumber || ''}
              citizenship={selectedGroup?.seafarerNationality || ''}
              dateOfBirth={selectedApplication?.cra?.seafarerDateOfBirth || ''}
              name={selectedApplication?.cra?.seafarerName || ''}
              validDate={selectedApplication?.cra?.expiresAt || ''}
              issuedAt={selectedApplication?.cra?.issuedWhere || ''}
              issuedDate={selectedApplication?.cra?.issuedAt || ''}
            />
          </div>
        </div>
      </StyledModal>
      <StyledModal visible={showDispensation} onCancel={onCloseDispensation}>
        <div>
          <Download>
            <TextLink
              onClick={downloadDispensation}
              text={intl.formatMessage({ id: 'general.downloadpdf' })}
            />
            {loadingDispensationPdf && <Loader size="24px" />}
          </Download>
          <div translate="no">
            <Dispensation
              applicationDate={selectedApplication?.dispensation?.issuedAt || ''}
              name={selectedApplication?.dispensation?.seafarerName || ''}
              dateOfBirth={selectedApplication?.dispensation?.seafarerDateOfBirth || ''}
              validDate={selectedApplication?.dispensation?.expiresAt || ''}
              citizenship={selectedGroup?.seafarerNationality || ''}
            />
          </div>
        </div>
      </StyledModal>
      <ClarifyModal
        application={clarifyApplication}
        error={clarifySubmitError}
        loading={clarifySubmitLoading}
        show={showClarifyModal}
        onClose={() => setShowClarifyModal(false)}
        onSubmit={onSubmitClarify}
      />
      <StyledModal visible={showEndorsement} onCancel={onCloseEndorsement}>
        <div>
          <Download>
            <TextLink
              onClick={downloadEndorsement}
              text={intl.formatMessage({ id: 'general.downloadpdf' })}
            />
            {loadingEndorsementPdf && <Loader size="24px" />}
          </Download>
          <div translate="no">
            <Endorsement
              authority={selectedApplication?.endorsement?.authority || ''}
              certificateNr={selectedApplication?.endorsement?.certificateNumber || ''}
              endorsementNr={selectedApplication?.endorsement?.endorsementNumber || ''}
              dateOfBirth={selectedApplication?.endorsement?.seafarerDateOfBirth || ''}
              name={selectedApplication?.endorsement?.seafarerName || ''}
              defaultLang="en"
              isStcwF={isStcwF}
              positionList={[
                {
                  position: selectedApplication?.endorsement?.title || '',
                  limitations: [localization.en['application.endorsement.limitation']],
                },
              ]}
              validDate={selectedApplication?.endorsement?.expiresAt || ''}
              issuedDate={selectedApplication?.endorsement?.issuedAt}
              qrImageLoading={false}
              // eslint-disable-next-line max-len
              qrData={`${process.env.REACT_APP_VERIFY_URL}/en/endorsement/${selectedApplication?.endorsement?.endorsementNumber}`}
              hideLanguageSelector
            />
          </div>
        </div>
      </StyledModal>
    </>
  );
};

export default HomePage;

const Applications = styled.div`
  margin: 40px 0;
`;

const Download = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 10px;
  height: 30px;
  button {
    margin-right: 10px;
  }
`;

const ActionBar = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;

  & > button {
    margin-right: auto;
  }
  & > div {
    margin-left: 5px;
  }

  #select-seafarer {
    min-width: 320px;
  }
  #select-status {
    min-width: 280px;
  }
`;

const ClearFilter = styled.div`
  margin-left: auto;
`;

const H1 = styled.h1`
  font-size: 4rem;
  font-family: ${styles.fonts.stfMedium};
  font-weight: normal;
`;

const ActionsWrapper = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  min-width: 105px;

  .delete-btn {
    margin-left: 5px;
  }
`;

const MobileFilter = styled.div`
  margin: 20px 0;
  display: none;
  flex-wrap: wrap;
  & > * {
    margin-right: 5px;
    margin-bottom: 5px;
  }

  @media screen and (max-width: ${({ theme }) => theme.screen.large}) {
    display: flex;
  }
`;

const ChildActions = styled.div`
  display: flex;
  button {
    min-width: fit-content;
    &:not(:last-child) {
      margin-right: 15px;
    }
  }
`;

const Applicant = styled.span`
  word-break: break-all;
`;

const EmptyAction = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  margin: 30px 0;
`;

const Reason = styled.span`
  white-space: pre-wrap;
`;

const StyledModal = styled(SdirModal)`
  max-height: 100vh;
  overflow-y: auto;
  -ms-overflow-style: none;
  scrollbar-width: none;
  p {
    margin: 0;
  }
`;
